import { useState, useEffect, useCallback } from 'react';

export interface ColumnConfig {
  id: string;
  label: string;
  sortKey: string | null; // null for non-sortable columns
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  visible: boolean;
  order: number;
  resizable: boolean;
  alwaysVisible?: boolean; // For columns like checkbox and menu
}

const STORAGE_KEY = 'mychat-drive-columns-config';

// Default column configuration
const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    id: 'checkbox',
    label: '',
    sortKey: null,
    defaultWidth: 20,
    minWidth: 20,
    maxWidth: 20,
    visible: true,
    order: 0,
    resizable: false,
    alwaysVisible: true,
  },
  {
    id: 'shortName',
    label: 'Nombre archivo resumido',
    sortKey: 'shortName',
    defaultWidth: 192, // w-48 = 192px
    minWidth: 100,
    maxWidth: 400,
    visible: true,
    order: 1,
    resizable: true,
  },
  {
    id: 'title',
    label: 'Nombre archivo original',
    sortKey: 'title',
    defaultWidth: 256, // w-64 = 256px
    minWidth: 150,
    maxWidth: 500,
    visible: true,
    order: 2,
    resizable: true,
  },
  {
    id: 'category',
    label: 'Categoría',
    sortKey: 'category',
    defaultWidth: 112, // w-28 = 112px
    minWidth: 80,
    maxWidth: 200,
    visible: true,
    order: 3,
    resizable: true,
  },
  {
    id: 'company',
    label: 'Empresa Original',
    sortKey: 'company',
    defaultWidth: 160, // w-40 = 160px
    minWidth: 100,
    maxWidth: 300,
    visible: true,
    order: 4,
    resizable: true,
  },
  {
    id: 'shortCompany',
    label: 'Empresa editado',
    sortKey: 'shortCompany',
    defaultWidth: 160, // w-40 = 160px
    minWidth: 100,
    maxWidth: 300,
    visible: true,
    order: 5,
    resizable: true,
  },
  {
    id: 'docType',
    label: 'Tipo Doc',
    sortKey: 'docType',
    defaultWidth: 112, // w-28 = 112px
    minWidth: 80,
    maxWidth: 200,
    visible: true,
    order: 6,
    resizable: true,
  },
  {
    id: 'period',
    label: 'Periodo editado',
    sortKey: 'period',
    defaultWidth: 96, // w-24 = 96px
    minWidth: 70,
    maxWidth: 150,
    visible: true,
    order: 7,
    resizable: true,
  },
  {
    id: 'periodOriginal',
    label: 'Periodo original',
    sortKey: 'period',
    defaultWidth: 96, // w-24 = 96px
    minWidth: 70,
    maxWidth: 150,
    visible: false, // Oculto por defecto, se puede activar
    order: 7.5,
    resizable: true,
  },
  {
    id: 'code',
    label: 'RUT',
    sortKey: 'code',
    defaultWidth: 128, // w-32 = 128px
    minWidth: 80,
    maxWidth: 200,
    visible: true,
    order: 8,
    resizable: true,
  },
  {
    id: 'sector',
    label: 'Sector',
    sortKey: 'sector',
    defaultWidth: 128, // w-32 = 128px
    minWidth: 80,
    maxWidth: 200,
    visible: false, // Hidden by default, user can enable
    order: 8.5,
    resizable: true,
  },
  {
    id: 'published',
    label: 'Publicado',
    sortKey: 'published',
    defaultWidth: 112, // w-28 = 112px
    minWidth: 90,
    maxWidth: 200,
    visible: false, // Hidden by default, user can enable
    order: 8.7,
    resizable: true,
  },
  {
    id: 'date',
    label: 'Fecha de carga',
    sortKey: 'date',
    defaultWidth: 112, // w-28 = 112px
    minWidth: 90,
    maxWidth: 200,
    visible: true,
    order: 9,
    resizable: true,
  },
  {
    id: 'size',
    label: 'Tamaño',
    sortKey: 'size',
    defaultWidth: 96, // w-24 = 96px
    minWidth: 70,
    maxWidth: 150,
    visible: true,
    order: 10,
    resizable: true,
  },
  {
    id: 'pages',
    label: 'Páginas',
    sortKey: 'pages',
    defaultWidth: 80, // w-20 = 80px
    minWidth: 60,
    maxWidth: 120,
    visible: true,
    order: 11,
    resizable: true,
  },
  {
    id: 'chunks',
    label: 'Chunks',
    sortKey: 'chunks',
    defaultWidth: 80, // w-20 = 80px
    minWidth: 60,
    maxWidth: 120,
    visible: true,
    order: 12,
    resizable: true,
  },
  {
    id: 'processing',
    label: 'Proc.',
    sortKey: 'processing',
    defaultWidth: 96, // w-24 = 96px
    minWidth: 70,
    maxWidth: 150,
    visible: true,
    order: 13,
    resizable: true,
  },
  {
    id: 'status',
    label: 'Estado',
    sortKey: 'status',
    defaultWidth: 112, // w-28 = 112px
    minWidth: 80,
    maxWidth: 200,
    visible: true,
    order: 14,
    resizable: true,
  },
  {
    id: 'menu',
    label: '',
    sortKey: null,
    defaultWidth: 32, // w-8 = 32px
    minWidth: 32,
    maxWidth: 32,
    visible: true,
    order: 15,
    resizable: false,
    alwaysVisible: true,
  },
];

export const useColumnManager = () => {
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Try to load from localStorage
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Merge with defaults to handle new columns
          const savedColumns = parsed.columns as ColumnConfig[];
          const merged = DEFAULT_COLUMNS.map((defaultCol) => {
            const savedCol = savedColumns.find((c) => c.id === defaultCol.id);
            if (savedCol) {
              return {
                ...defaultCol,
                ...savedCol,
                // Always use label from default (don't override with saved label)
                label: defaultCol.label,
                // Ensure alwaysVisible columns stay visible
                visible: defaultCol.alwaysVisible ? true : savedCol.visible,
                // Ensure min/max widths are respected
                defaultWidth: Math.max(
                  defaultCol.minWidth,
                  Math.min(defaultCol.maxWidth, savedCol.defaultWidth || defaultCol.defaultWidth)
                ),
              };
            }
            return defaultCol;
          });
          // Sort by order
          merged.sort((a, b) => a.order - b.order);
          return merged;
        }
      } catch (error) {
        console.warn('Error loading column configuration:', error);
      }
    }
    return DEFAULT_COLUMNS;
  });

  // Save to localStorage whenever columns change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ columns }));
      } catch (error) {
        console.warn('Error saving column configuration:', error);
      }
    }
  }, [columns]);

  // Get visible columns in order
  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((columnId: string) => {
    setColumns((prev) =>
      prev.map((col) => {
        if (col.id === columnId && !col.alwaysVisible) {
          return { ...col, visible: !col.visible };
        }
        return col;
      })
    );
  }, []);

  // Reorder columns
  const reorderColumns = useCallback((draggedId: string, targetOrder: number) => {
    setColumns((prev) => {
      const dragged = prev.find((col) => col.id === draggedId);
      if (!dragged) return prev;

      const newColumns = prev.map((col) => {
        if (col.id === draggedId) {
          return { ...col, order: targetOrder };
        }
        // Shift other columns
        if (dragged.order < targetOrder) {
          // Moving right
          if (col.order > dragged.order && col.order <= targetOrder) {
            return { ...col, order: col.order - 1 };
          }
        } else {
          // Moving left
          if (col.order >= targetOrder && col.order < dragged.order) {
            return { ...col, order: col.order + 1 };
          }
        }
        return col;
      });

      return newColumns.sort((a, b) => a.order - b.order);
    });
  }, []);

  // Resize column
  const resizeColumn = useCallback((columnId: string, newWidth: number) => {
    setColumns((prev) =>
      prev.map((col) => {
        if (col.id === columnId && col.resizable) {
          const clampedWidth = Math.max(col.minWidth, Math.min(col.maxWidth, newWidth));
          return { ...col, defaultWidth: clampedWidth };
        }
        return col;
      })
    );
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setColumns(DEFAULT_COLUMNS);
  }, []);

  // Get column by ID
  const getColumn = useCallback(
    (columnId: string) => columns.find((col) => col.id === columnId),
    [columns]
  );

  return {
    columns,
    visibleColumns,
    toggleColumnVisibility,
    reorderColumns,
    resizeColumn,
    resetToDefaults,
    getColumn,
  };
};
